// apps/codeframe/src/motion/player.ts
import type { MotionTimeline } from '../store/types';
import { sampleFrame } from './sample';
import { applyToRoot } from './apply';
import { resolveColorTokens, hasVarColors } from './resolveColorTokens';
import { invalidateCanvas } from '../render/canvasInvalidate';
import { resolveCssColor } from '../chrome/resolveCssColor';

export class MotionPlayer {
  time = 0;
  playing = false;
  private dir: 1 | -1 = 1;
  private scrubbing = false;
  private reset: (() => void) | null = null;
  private hasVar: boolean;
  /** Cache of var()/token color string → resolved rgb; cleared on theme change. */
  private colorCache = new Map<string, string>();

  constructor(private tl: MotionTimeline, private root: HTMLElement) {
    this.hasVar = hasVarColors(tl);
  }

  setTimeline(tl: MotionTimeline) { this.tl = tl; this.hasVar = hasVarColors(tl); this.apply(); }

  /** Re-resolve theme-token colors (call on light/dark or active-theme change). */
  refreshTheme() { this.colorCache.clear(); this.apply(); }

  /** Resolve a `var(--color-x)` value to a concrete color (cached per player). */
  private resolveColorValue = (raw: string): string => {
    if (!raw.includes('var(')) return raw;
    const cached = this.colorCache.get(raw);
    if (cached !== undefined) return cached;
    const out = resolveCssColor(raw, this.root);
    this.colorCache.set(raw, out);
    return out;
  };

  private apply() {
    const tl = this.hasVar ? resolveColorTokens(this.tl, this.resolveColorValue) : this.tl;
    this.reset?.();
    this.reset = applyToRoot(this.root, sampleFrame(tl, this.time));
    invalidateCanvas();
  }

  seek(t: number) { this.time = clamp(t, 0, this.tl.duration); this.apply(); }
  setScrubbing(on: boolean) { this.scrubbing = on; }
  play() {
    this.dir = 1;
    // Reduced-motion: a CSS guard can't stop the JS motion engine, so snap to the
    // final keyframe (the rest state) and never start the autoplay tween (B5).
    if (prefersReducedMotion()) { this.seek(this.tl.duration); this.playing = false; return; }
    if ((this.tl.playback.autoplay?.mode ?? 'loop') === 'once' && this.time >= this.tl.duration) this.time = 0;
    this.playing = true;
  }
  reverse() { this.playing = true; this.dir = -1; }
  pause() { this.playing = false; }
  goto(seg: [number, number]) { this.seek(seg[0]); }

  /** Advance by dt ms. Clock is suspended while scrubbing. */
  tick(dt: number) {
    if (!this.playing || this.scrubbing || this.tl.duration <= 0) return;
    const mode = this.tl.playback.autoplay?.mode ?? 'loop';
    let t = this.time + dt * this.dir;
    const D = this.tl.duration;
    if (mode === 'loop') { t = ((t % D) + D) % D; }
    else if (mode === 'pingpong') { if (t > D) { t = D - (t - D); this.dir = -1; } else if (t < 0) { t = -t; this.dir = 1; } }
    else { t = clamp(t, 0, D); if (t >= D) this.playing = false; }
    this.time = t;
    this.apply();
  }

  destroy() { this.reset?.(); this.reset = null; this.playing = false; }
}

function clamp(v: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, v)); }

/** True when the OS requests reduced motion. Safe (false) when matchMedia is absent. */
function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
}
